import { z } from "zod";

/** How a Werkio workspace was created and what data it contains. */
export const WORKSPACE_TYPES = ["DEMO", "BETA", "PRODUCTION"] as const;
export type WorkspaceType = (typeof WORKSPACE_TYPES)[number];

export const ONBOARDING_STATUSES = ["NOT_STARTED", "IN_PROGRESS", "COMPLETE"] as const;
export type OnboardingStatus = (typeof ONBOARDING_STATUSES)[number];

export const workspaceTypeSchema = z.enum(WORKSPACE_TYPES);
export const onboardingStatusSchema = z.enum(ONBOARDING_STATUSES);

export const workspaceSummarySchema = z.object({
  type: workspaceTypeSchema,
  onboardingStatus: onboardingStatusSchema,
  setupCompletedAt: z.string().datetime().nullable(),
  companyName: z.string(),
  companySlug: z.string(),
  needsOnboarding: z.boolean(),
});

export type WorkspaceSummary = z.infer<typeof workspaceSummarySchema>;

export const createBetaWorkspaceSchema = z.object({
  companyName: z.string().trim().min(2).max(120),
  timezone: z.string().trim().min(1).max(64).default("America/Los_Angeles"),
});

export type CreateBetaWorkspaceInput = z.infer<typeof createBetaWorkspaceSchema>;

export const completeOnboardingSchema = z.object({
  timezone: z.string().trim().min(1).max(64).optional(),
});

export type CompleteOnboardingInput = z.infer<typeof completeOnboardingSchema>;

export {
  BETA_CURRENCY_OPTIONS,
  BETA_PAY_PERIOD_OPTIONS,
  BETA_PAY_RULE_TEMPLATE_LABELS,
  BETA_PAY_RULE_TEMPLATES,
  BETA_SETUP_PATH_LABELS,
  BETA_SETUP_PATHS,
  DEFAULT_BETA_GEOFENCE_RADIUS_METERS,
  buildStarterPayRuleProfile,
  buildStarterPayRuleRows,
  finishBetaOnboardingSchema,
  resolveBetaOnboardingStatus,
  type BetaPayRuleTemplate,
  type BetaSetupPath,
  type FinishBetaOnboardingInput,
} from "./beta-onboarding.js";

export {
  SETUP_CHECKLIST_COPY,
  SETUP_CHECKLIST_STEP_KEYS,
  buildSetupChecklistHref,
  isWorkspaceSetupComplete,
  resolveWorkspaceSetupPhase,
  hasWorkspaceRealData,
  workspaceSetupProgressSchema,
  WORKSPACE_SETUP_PHASES,
  type WorkspaceSetupPhase,
  type WorkspaceSetupProgress,
  setupChecklistStepSchema,
  workspaceSetupStatusSchema,
  type SetupChecklistStep,
  type SetupChecklistStepKey,
  type WorkspaceSetupStatus,
} from "./setup-checklist.js";

export const getStartedEligibilitySchema = z.object({
  eligible: z.boolean(),
  reason: z
    .enum([
      "ALLOWLISTED",
      "NOT_ALLOWLISTED",
      "ALLOWLIST_BLOCKED",
      "ALREADY_PROVISIONED",
      "EMAIL_REQUIRED",
      "GET_STARTED_DISABLED",
    ])
    .optional(),
  message: z.string().optional(),
});

export type GetStartedEligibility = z.infer<typeof getStartedEligibilitySchema>;

/** Workspace types that use the guided onboarding flow before full app access. */
export const ONBOARDING_WORKSPACE_TYPES: readonly WorkspaceType[] = ["BETA"];

export function workspaceNeedsOnboarding(
  type: WorkspaceType,
  status: OnboardingStatus,
): boolean {
  return ONBOARDING_WORKSPACE_TYPES.includes(type) && status !== "COMPLETE";
}

export const WORKSPACE_ERRORS = {
  NOT_ALLOWLISTED:
    "This email is not on the Werkio beta allowlist. Request access or contact support.",
  ALREADY_PROVISIONED: "You already belong to a Werkio workspace.",
  GET_STARTED_DISABLED: "Self-serve workspace creation is not available right now.",
} as const;
